"use client";

import { useState } from "react";
import Link from "next/link";

import { AuditReportPreview } from "~/components/siteglue/home/home-hero-report-preview";
import { FadeIn, HomeEyebrow, HomeHeading, HomeLead, HomeSection } from "~/components/siteglue/home/section-shell";
import { AUDIT_SERVICE_PAGES } from "~/lib/siteglue/audits/catalog";
import {
  HOMEPAGE_AUDITS_EYEBROW,
  HOMEPAGE_AUDITS_HEADING,
} from "~/lib/siteglue/landing-redesign/copy";
import {
  homepagePreviewSlide,
  OPTIMIZATION_SERVICE_TAB_ORDER,
  type HomepagePreviewSlideId,
} from "~/lib/siteglue/landing-redesign/homepage-hero";
import { PUBLIC_PRICING_CARDS } from "~/lib/siteglue/public-pricing";

const TAB_PAGE_SLUG: Record<HomepagePreviewSlideId, string> = {
  aro: "agent-readiness",
  aeo: "answer-engine-optimization",
  cro: "conversion-optimization",
};

const TAB_LABELS: Record<HomepagePreviewSlideId, { short: string; full: string }> = {
  aro: { short: "ARO", full: "Agent Readiness" },
  aeo: { short: "AEO", full: "Answer Engine Optimization" },
  cro: { short: "CRO", full: "Conversion Optimization" },
};

const MAX_BENCHMARKS = 5;

export function HomeOptimizationServices() {
  const [active, setActive] = useState<HomepagePreviewSlideId>("aro");
  const page = AUDIT_SERVICE_PAGES.find((item) => item.slug === TAB_PAGE_SLUG[active]);
  const pricing = PUBLIC_PRICING_CARDS.find((card) => card.id === active);
  const slide = homepagePreviewSlide(active);
  const benchmarks = page?.benchmarks?.slice(0, MAX_BENCHMARKS) ?? [];

  return (
    <HomeSection tone="slate" className="py-16 sm:py-20">
      <FadeIn>
        <HomeEyebrow>{HOMEPAGE_AUDITS_EYEBROW}</HomeEyebrow>
        <HomeHeading>{HOMEPAGE_AUDITS_HEADING}</HomeHeading>
        <HomeLead>
          Three free audits around one SiteGlue Agent — ARO, AEO, and CRO. Pick an audit to see
          what it measures, then run it on your website.
        </HomeLead>
      </FadeIn>
      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <div className="min-w-0">
          <div
            role="tablist"
            aria-label="Free AI optimization audits"
            className="flex gap-2 overflow-x-auto"
          >
            {OPTIMIZATION_SERVICE_TAB_ORDER.map((id) => {
              const selected = active === id;
              const label = TAB_LABELS[id];
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  id={`optimization-tab-${id}`}
                  aria-selected={selected}
                  aria-controls={`optimization-panel-${id}`}
                  onClick={() => setActive(id)}
                  className={[
                    "min-w-[8.5rem] flex-1 rounded-2xl border px-4 py-3 text-left transition",
                    selected
                      ? "border-[#4f81bd] bg-white shadow-[0_8px_20px_rgba(79,129,189,0.08)]"
                      : "border-slate-200 bg-white/70 hover:border-slate-300 hover:bg-white",
                  ].join(" ")}
                >
                  <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
                    {label.short}
                  </span>
                  <span className="mt-1 block text-sm font-semibold text-slate-900">{label.full}</span>
                </button>
              );
            })}
          </div>
          {page ? (
            <div
              role="tabpanel"
              id={`optimization-panel-${active}`}
              aria-labelledby={`optimization-tab-${active}`}
              className="mt-4 rounded-2xl border border-slate-200 bg-white p-6"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">{page.eyebrow}</p>
              <h3 className="mt-3 text-xl font-semibold text-slate-900">{page.headline}</h3>
              {pricing ? (
                <p className="mt-2 text-sm font-semibold text-slate-900">{pricing.priceLabel}</p>
              ) : null}
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{page.description}</p>
              {benchmarks.length > 0 ? (
                <ul className="mt-5 space-y-3">
                  {benchmarks.map((item) => (
                    <li key={item.title}>
                      <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                      <p className="mt-0.5 text-sm leading-relaxed text-slate-600">{item.body}</p>
                    </li>
                  ))}
                </ul>
              ) : null}
              <Link
                href={page.href}
                className="mt-5 inline-flex text-sm font-semibold text-[#4f81bd] hover:underline"
              >
                {page.ctaLabel}
              </Link>
            </div>
          ) : null}
        </div>
        {slide ? <AuditReportPreview slide={slide} /> : null}
      </div>
    </HomeSection>
  );
}
